import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, LinearProgress } from '@mui/material';
import { DateTime } from 'luxon';

import { AppDispatch } from '../../../data/store';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

import { fetchTransportationAttributesAsync } from '../../../data/accounting.slice';
import {
  fetchPassengerClientDataAsync,
  selectPassengerClientData,
  setLoadedPassengerClientData,
} from '../../../data/client.slice';
import {
  selectLoadingCurrentPassenger,
  selectCurrentPassengerPastReservations,
  selectCurrentPassengerFutureReservations,
  selectPassengerEventEmitted,
  selectCurrentPassenger,
  fetchPassengerFutureReservationsAsync,
  fetchPassengerPastReservationsAsync,
  loadMorePassengerPastReservationsAsync,
  loadMorePassengerFutureReservationsAsync,
  fetchPassengerAsync,
  setCurrentPassenger,
  setPassengerEventEmitted,
} from '../../../data/passenger.slice';
import {
  fetchPassengerBenefitSummaryAsync,
  selectCanceledReservation,
  selectCanceledTripLeg,
  selectReservationEventEmitted,
  selectSavedReservation,
  selectSavedTripLeg,
  setReservationEventEmitted,
} from '../../../data/reservation.slice';
import {
  selectTripLegEventEmitted,
  setTripLegEventEmitted,
} from '../../../data/tripleg.slice';
import PassengerHeader from './PassengerHeader';
import PassengerDetails from './PassengerDetails';
import ReservationDialog from '../reservations/ReservationDialog';

const PREFIX = 'Passenger';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
}));

export default function Passenger() {
  const dispatch: AppDispatch = useAppDispatch();
  const t = useCommonTranslation();
  const params = useParams<{ id: string }>();
  const passenger = useAppSelector(selectCurrentPassenger);
  const savedReservation = useAppSelector(selectSavedReservation);
  const savedTripLeg = useAppSelector(selectSavedTripLeg);
  const canceledReservation = useAppSelector(selectCanceledReservation);
  const canceledTripLeg = useAppSelector(selectCanceledTripLeg);
  const loadingPassenger = useAppSelector(selectLoadingCurrentPassenger);
  const loadedCurrentPassenger = params.id === passenger?.passengerUUID;
  const pastReservations = useAppSelector(
    selectCurrentPassengerPastReservations,
  );
  const futureReservations = useAppSelector(
    selectCurrentPassengerFutureReservations,
  );
  const passengerClientData = useAppSelector(selectPassengerClientData);
  const isSuperReaderUser = useIsSuperReaderUser();
  const passengerEventDetail = useAppSelector(selectPassengerEventEmitted);
  const reservationEventDetail = useAppSelector(selectReservationEventEmitted);
  const tripLegEventDetail = useAppSelector(selectTripLegEventEmitted);

  const [tripDialogOpen, setTripDialogOpen] = useState(false);

  const fetchPassengerBenefitSummary = useCallback(() => {
    if (params.id && passenger?.passengerUUID) {
      dispatch(
        fetchPassengerBenefitSummaryAsync(
          passenger.passengerUUID,
          passenger.servicePlans.map(({ servicePlanUUID }) => servicePlanUUID),
          DateTime.now().toISODate(),
        ),
      );
    }
  }, [params.id, passenger, dispatch]);

  const fetchPassengerFutureReservations = useCallback(() => {
    if (params.id) {
      dispatch(fetchPassengerFutureReservationsAsync(params.id, 100));
    }
  }, [params.id, dispatch]);

  const fetchPassengerPastReservations = useCallback(() => {
    if (params.id) {
      dispatch(fetchPassengerPastReservationsAsync(params.id, 100));
    }
  }, [params.id, dispatch]);

  const loadMorePastReservations = useCallback(() => {
    if (params.id && pastReservations.continuationToken) {
      dispatch(
        loadMorePassengerPastReservationsAsync(
          params.id,
          100,
          pastReservations.continuationToken,
        ),
      );
    }
  }, [params.id, pastReservations.continuationToken, dispatch]);

  const loadMoreFutureReservations = useCallback(() => {
    if (params.id && futureReservations.continuationToken) {
      dispatch(
        loadMorePassengerFutureReservationsAsync(
          params.id,
          100,
          futureReservations.continuationToken,
        ),
      );
    }
  }, [params.id, futureReservations.continuationToken, dispatch]);

  useEffect(() => {
    dispatch(fetchTransportationAttributesAsync());
  }, []);

  useEffect(() => {
    if (params.id) {
      dispatch(setLoadedPassengerClientData(false));
      dispatch(fetchPassengerAsync(params.id));
      fetchPassengerFutureReservations();
      fetchPassengerPastReservations();
    }
  }, [params.id, dispatch]);

  useEffect(() => {
    if (loadedCurrentPassenger && passenger) {
      dispatch(
        fetchPassengerClientDataAsync(
          passenger.clientBC,
          passenger.servicePlans,
        ),
      );
    }
  }, [loadedCurrentPassenger, passenger]);

  useEffect(() => {
    const reservationChanged =
      savedReservation ||
      savedTripLeg ||
      canceledReservation ||
      canceledTripLeg;
    if (passenger?.passengerUUID && reservationChanged) {
      fetchPassengerBenefitSummary();
    }
  }, [
    passenger,
    savedReservation,
    savedTripLeg,
    canceledReservation,
    canceledTripLeg,
    fetchPassengerBenefitSummary,
  ]);

  useEffect(() => {
    if (
      passenger?.passengerUUID &&
      passengerEventDetail?.passengerUUID === passenger?.passengerUUID
    ) {
      dispatch(setCurrentPassenger(passengerEventDetail));
      dispatch(setPassengerEventEmitted(null));
    }
  }, [passengerEventDetail]);

  useEffect(() => {
    if (
      passenger?.passengerUUID &&
      reservationEventDetail?.passengerUUID === passenger?.passengerUUID
    ) {
      fetchPassengerFutureReservations();
      fetchPassengerPastReservations();
      fetchPassengerBenefitSummary();
      dispatch(setReservationEventEmitted(null));
    }
  }, [
    reservationEventDetail,
    fetchPassengerBenefitSummary,
    fetchPassengerFutureReservations,
    fetchPassengerPastReservations,
  ]);

  useEffect(() => {
    if (
      passenger?.passengerUUID &&
      tripLegEventDetail?.detail.passengerUUID === passenger?.passengerUUID
    ) {
      fetchPassengerFutureReservations();
      fetchPassengerPastReservations();
      dispatch(setTripLegEventEmitted(null));
    }
  }, [tripLegEventDetail]);

  return (
    <StyledBox className={classes.container}>
      {loadingPassenger && <LinearProgress color="primary" />}
      {!loadingPassenger && (
        <PassengerHeader
          name={`${[passenger?.firstName, passenger?.middleName, passenger?.lastName].filter(Boolean).join(' ') || ''}`}
          createDisabled={!passenger}
          createHidden={isSuperReaderUser}
          onCreateTrip={() => setTripDialogOpen(true)}
        />
      )}
      <PassengerDetails
        passenger={passenger}
        loadingPassenger={loadingPassenger}
        pastReservations={pastReservations}
        futureReservations={futureReservations}
        servicePlans={passengerClientData?.servicePlans || []}
        onLoadMorePastReservations={loadMorePastReservations}
        onLoadMoreFutureReservations={loadMoreFutureReservations}
      />
      {passenger && tripDialogOpen && (
        <ReservationDialog
          title={t('reservation.createTrip')}
          open={tripDialogOpen}
          passenger={passenger!}
          servicePlans={passengerClientData?.servicePlans || []}
          close={() => setTripDialogOpen(false)}
          isPortal={false}
        />
      )}
    </StyledBox>
  );
}
